import { useState } from 'react'
import { Button, Modal } from '@/components/ui'
import { ExitTypeSelect } from '@/components/common/ExitTypeSelect'
import type { ExitType } from '@/lib/exitTypes'

export interface RemoveCompanyMemberChoice {
  exitType: ExitType
  reason?: string
}

export interface RemoveCompanyMemberModalProps {
  open: boolean
  memberName: string
  companyName: string
  loading?: boolean
  onClose: () => void
  onConfirm: (choice: RemoveCompanyMemberChoice) => void
}

/**
 * Removing a member ends their People & HR employment in the company, so the
 * admin must say why (exit type), with an optional note.
 */
export function RemoveCompanyMemberModal({
  open,
  memberName,
  companyName,
  loading = false,
  onClose,
  onConfirm,
}: RemoveCompanyMemberModalProps): React.ReactElement {
  const [exitType, setExitType] = useState<ExitType | ''>('')
  const [reason, setReason] = useState('')

  const [wasOpen, setWasOpen] = useState(open)
  // Each opening starts from a blank choice (state adjusted during render, not in an effect).
  if (open !== wasOpen) {
    setWasOpen(open)
    if (open) {
      setExitType('')
      setReason('')
    }
  }

  const submit = (): void => {
    if (!exitType) return
    const note = reason.trim()
    onConfirm(note ? { exitType, reason: note } : { exitType })
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Remove member"
      className="max-w-md"
      footer={
        <>
          <Button type="button" variant="ghost" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            type="button"
            variant="danger"
            onClick={submit}
            disabled={!exitType}
            loading={loading}
          >
            Remove
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Remove {memberName} from {companyName}? Their People &amp; HR employment in this company
          ends today.
        </p>
        <ExitTypeSelect id="remove-member-exit-type" value={exitType} onChange={setExitType} />
        <label className="block">
          <span className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
            Reason (optional)
          </span>
          <textarea
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            rows={3}
            maxLength={500}
            className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100"
          />
        </label>
      </div>
    </Modal>
  )
}

RemoveCompanyMemberModal.displayName = 'RemoveCompanyMemberModal'
